import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ---------------------------------------------------------------------------
// Point types (plain objects, serializable for Redux)
// ---------------------------------------------------------------------------

export type TwapPoint = {
  timestamp: string;
  twap1m?: number;
  twap5m?: number;
  twap15m?: number;
  twap30m?: number;
  twap1h?: number;
  twap4h?: number;
  lastPrice?: number;
};

export type VwapPoint = {
  timestamp: string;
  vwap: number;
};

export type MicropricePoint = {
  timestamp: string;
  microprice: number;
  imbalance: number;
  spread?: number;
  bidPrice?: number;
  askPrice?: number;
};

export type LastPricePoint = {
  timestamp: string;
  lastPrice: number;
};

// ---------------------------------------------------------------------------
// Slice state
// ---------------------------------------------------------------------------

export interface ChartDataState {
  twap: Record<string, TwapPoint[]>;
  vwap: Record<string, VwapPoint[]>;
  microprice: Record<string, MicropricePoint[]>;
  lastPrice: Record<string, LastPricePoint[]>;
}

const initialState: ChartDataState = {
  twap: {},
  vwap: {},
  microprice: {},
  lastPrice: {},
};

const MAX_TWAP = 200;
const MAX_VWAP = 200;
const MAX_LAST_PRICE = 200;
const MAX_MICROPRICE = 200;  // cap like the other series; was unbounded (browser memory leak on long sessions)

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const chartDataSlice = createSlice({
  name: 'chartData',
  initialState,
  reducers: {
    appendTwapPoint(state, action: PayloadAction<{ secId: string; point: TwapPoint }>) {
      const { secId, point } = action.payload;
      const arr = state.twap[secId] ?? [];
      if (arr.some(p => p.timestamp === point.timestamp)) return;
      state.twap[secId] = [...arr, point].slice(-MAX_TWAP);
    },
    appendVwapPoint(state, action: PayloadAction<{ secId: string; point: VwapPoint }>) {
      const { secId, point } = action.payload;
      const arr = state.vwap[secId] ?? [];
      if (arr.some(p => p.timestamp === point.timestamp)) return;
      state.vwap[secId] = [...arr, point].slice(-MAX_VWAP);
    },
    appendMicropricePoint(state, action: PayloadAction<{ secId: string; point: MicropricePoint }>) {
      const { secId, point } = action.payload;
      const arr = state.microprice[secId] ?? [];
      if (arr.some(p => p.timestamp === point.timestamp)) return;
      state.microprice[secId] = [...arr, point].slice(-MAX_MICROPRICE);
    },
    appendLastPricePoint(state, action: PayloadAction<{ secId: string; point: LastPricePoint }>) {
      const { secId, point } = action.payload;
      const arr = state.lastPrice[secId] ?? [];
      state.lastPrice[secId] = [...arr, point].slice(-MAX_LAST_PRICE);
    },
    // Bulk-replace actions used by the replay/history loader
    setTwapPoints(state, action: PayloadAction<{ secId: string; points: TwapPoint[] }>) {
      state.twap[action.payload.secId] = action.payload.points;
    },
    setVwapPoints(state, action: PayloadAction<{ secId: string; points: VwapPoint[] }>) {
      state.vwap[action.payload.secId] = action.payload.points;
    },
    setMicropricePoints(state, action: PayloadAction<{ secId: string; points: MicropricePoint[] }>) {
      state.microprice[action.payload.secId] = action.payload.points;
    },
    setLastPricePoints(state, action: PayloadAction<{ secId: string; points: LastPricePoint[] }>) {
      state.lastPrice[action.payload.secId] = action.payload.points;
    },
  },
});

export const {
  appendTwapPoint,
  appendVwapPoint,
  appendMicropricePoint,
  appendLastPricePoint,
  setTwapPoints,
  setVwapPoints,
  setMicropricePoints,
  setLastPricePoints,
} = chartDataSlice.actions;

export default chartDataSlice.reducer;
